import React, {useState} from 'react';
import {
    Autocomplete,
    Box, Button,
    Card,
    CardContent,
    Grid, IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {Team} from "../../../../../types/teams.types";
import {DeleteForever} from "@mui/icons-material";
import { When } from "react-if";
import palette from "../../../../../theme/palette";
import {CustomPopper} from "../../../../../components/CustomPopper";

interface Props {
    allTeams : Team[];
    setSelectedTeams: (teams: Team[]) => void;
    handleNext: () => void;
    selectedTeams: Team[]
    isTeamsLoading : boolean;
}

const styles = {
    selectContainer: {
        marginTop: '16px',
        borderRadius: 1,
        border: '0.3px solid',
        overflow: 'auto',
        p: 2,
        gap:'15px',
        maxHeight: '300px',
        minHeight: '120px',
        borderColor:'#ccc'
    },
    teamSelectItemName: {
        fontSize: '16px',
        display:'inline',
        color: '#919EAB',
        fontWeight: '400',
        fontFamily: 'Poppins, sans-serif',
        ml: 1.5,
    },
    teamSelectContainer: {
        ':hover': {
            background: '#F5F6F8',
            cursor: 'pointer',
        },
        background:'#ecf2fa',
        border: '0.1px solid #ccc',
        borderRadius: 1
    },
    selectItemName: {
        fontSize: '16px',
        color: '#919EAB',
        fontWeight: '400',
        fontFamily: 'Poppins, sans-serif',
        ml: 1.5,
    },
    selectItemContainer: {
        p: 1,
        ':hover': {
            background: '#F5F6F8',
            cursor: 'pointer',
        },
        height: '60px',
        zIndex: 9999
    }
}

export const TeamSelect: React.FC<Props> = ({allTeams,
                                                setSelectedTeams,
                                                selectedTeams,
                                                isTeamsLoading,
                                                handleNext
}) => {

    const [inputVal, setInputVal] = useState<string>('');
    const [selectAllText, setSelectAllText] = useState<string>('Select All');

    const handleCancel = () => {
        setSelectedTeams([]);
        setSelectAllText('Select All')

    }

    const handleConfirm = () => {
        handleNext();
    }

    const teamIsSelected = (team: Team) => {
        return selectedTeams.filter(t => (t as any)?.id === (team as any)?.id).length > 0;
    }

    const filterOptions = (options : Team[],inputValue : string) => {
        return options.filter((team) => {
            return team?.name?.toLowerCase()?.includes(inputValue.toLowerCase()) || (team as any)?.id === 'all';
        })
    }

    const selectTeam = (team : Team) => {
        if((team as any)?.id === 'all'){
            if(selectedTeams.length === allTeams.length - 1){
                setSelectedTeams([]);
                setSelectAllText('Select All')
            }else{
                setSelectAllText('Deselect All')
                setSelectedTeams(allTeams.filter((team) => (team as any)?.id !== 'all'));
            }
        }else{
            if (teamIsSelected(team!)) {
                setSelectedTeams(selectedTeams.filter((t) => (t as any)?.id !== (team as any)?.id));
            } else {
                setSelectedTeams([...selectedTeams, team]);
            }
        }
    }

    const selectAllOptions = {
        _id:'all',
        id:'all',
        name:selectAllText,
        tags: [],
        settings: {},
        users: [],
    };
    allTeams = [selectAllOptions,...allTeams];


    return (
        <Stack margin={'auto'}>
            <Card>
                <CardContent sx={{padding:5}}>
                    <Typography fontWeight={500} fontSize={18} sx={{mb:2}}>
                        Select the Team(s) you would like to Review
                    </Typography>
                    <Box sx={{mb:2}}>
                        <Typography fontWeight={400} fontSize={16} color={'text.secondary'}>
                            Select one or many teams to review
                        </Typography>
                    </Box>
                    <Stack sx={{width: {md:'800px',xs:'auto'}}}>
                        <Grid container alignContent={'center'} justifyContent={'left'} alignItems={'center'}>
                            <Grid item md={8} xs={12} alignContent={'center'} justifyItems={'center'} sx={{'overflow':'visible' }}>
                                <Autocomplete
                                    id="autocomplete-input-risk-review-team"
                                    disablePortal
                                    filterOptions={(options : Team[], {inputValue}) => {
                                        return filterOptions(options, inputValue);
                                    }}
                                    sx={{ width: '100%'}}
                                    PopperComponent={CustomPopper}
                                    renderInput={(params) =>
                                        <TextField
                                            {...params}
                                            sx={{ marginTop: '16px' }}
                                            fullWidth
                                            label="Team Name"
                                        />
                                    }
                                    getOptionLabel={(option) => `${option.name}`}
                                    onChange={(e, value) => {
                                        return undefined
                                    }}
                                    onInputChange={(e, value) => {
                                        setInputVal(value);
                                    }}
                                    noOptionsText={inputVal.length > 0 ? 'No teams found' : 'Start typing to search teams'}
                                    options={allTeams || []}
                                    renderOption={(props, option) => !teamIsSelected(option) && (
                                        <Grid
                                            container
                                            key={(option as any)?.id}
                                            alignContent={'center'}
                                            alignItems={'center'}
                                            sx={{
                                                ...styles.selectItemContainer,
                                                borderRadius: 1,
                                                background: 'white'
                                            }}
                                            onClick={() => {
                                                selectTeam(option);
                                            }}
                                        >
                                            <When condition={option.id === 'all'}>
                                                <Typography sx={{color:'text.primary', fontWeight:500, ml:2}}>
                                                    {option?.name}
                                                </Typography>
                                            </When>
                                            <When condition={option.id !== 'all'}>
                                                <Typography sx={styles.selectItemName}>
                                                    {option?.name}
                                                </Typography>
                                            </When>
                                        </Grid>
                                    )}
                                />
                            </Grid>
                        </Grid>
                                <Grid
                                    container
                                    mb={3}
                                    alignContent={'center'}
                                    alignItems={'center'}
                                    sx={{
                                        ...styles.selectContainer
                                    }}
                                >
                                    {!isTeamsLoading && selectedTeams?.map((team) => (
                                <Grid
                                    item
                                    key={team?.id}
                                    sx={styles.teamSelectContainer}
                                >
                                    <Box>
                                        <Typography sx={styles.teamSelectItemName}>
                                            {team?.name}
                                        </Typography>
                                        <Tooltip title="Remove team">
                                            <IconButton
                                                sx={{ ml: 1 }}
                                                onClick={() => {
                                                    setSelectedTeams(selectedTeams.filter((t) => (t as any)?.id !== (team as any)?.id));
                                                }}
                                            >
                                                <DeleteForever color="action" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                        <Box sx={{}}>
                            <Grid
                                marginTop={20}
                                flexDirection={'row-reverse'}
                                container
                                spacing={3}
                                mt={3}
                            >
                                <Button disabled={selectedTeams.length===0} sx={{color:palette.darkBlue}} onClick={handleConfirm}>
                                    Confirm
                                </Button>
                                <Button disabled={false} color="inherit" onClick={handleCancel}>
                                    Cancel
                                </Button>
                            </Grid>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>
        </Stack>
    )
}